/* eslint-disable @typescript-eslint/no-explicit-any */
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { PRODGY_CHART_COLORS } from '@/shared/constants/colors';
import { useChartTheme } from './chartTheme';

interface BarChartProps {
  data: any;
  config: {
    xAxis?: string;
    yAxis?: string;
    xField?: string;
    yField?: string;
    groupBy?: string;
    stacked?: boolean;
    percentage?: boolean;
  };
}

export const BarChart = ({ data, config }: BarChartProps) => {
  const chart = useChartTheme();

  const option: EChartsOption = {
    color: PRODGY_CHART_COLORS,
    backgroundColor: chart.background,
    textStyle: { color: chart.axisLabel },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: chart.tooltipBg,
      borderColor: chart.tooltipBorder,
      textStyle: {
        color: chart.tooltipText,
      },
      // For grouped/stacked bars the default axis tooltip lists every series at
      // the hovered x — including series whose value is 0 for that category.
      // That confuses readers ("openrouter has 0 of gpt-4o" → people think
      // gpt-4o was ever used there). Skip zero rows; preserve the percentage
      // formatter when configured.
      formatter: (raw: unknown) => {
        const params = Array.isArray(raw) ? raw : [];
        const visible = params.filter((p: any) => {
          const v = Number(p?.value);
          return Number.isFinite(v) && v !== 0;
        });
        if (visible.length === 0) return '';
        const fmt = config.percentage
          ? (v: any) => `${Number(v).toFixed(1)}%`
          : (v: any) => Number(v).toLocaleString();
        const head = `<div style="margin-bottom:4px;font-weight:600">${(visible[0] as any).axisValue ?? ''}</div>`;
        const rows = visible.map((p: any) =>
          `<div style="display:flex;align-items:center;gap:6px">${p.marker}<span style="flex:1">${p.seriesName}</span><b>${fmt(p.value)}</b></div>`
        ).join('');
        return head + rows;
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: data.series?.length > 1 ? '12%' : '3%',
      top: '3%',
      containLabel: true,
    },
    legend: data.series?.length > 1 ? {
      bottom: '0%',
      textStyle: {
        color: chart.axisLabel,
      },
    } : undefined,
    xAxis: {
      type: 'category',
      data: data.labels || data.categories || [],
      axisLine: {
        lineStyle: {
          color: chart.axisLine,
        },
      },
      axisLabel: {
        color: chart.axisLabel,
      },
    },
    yAxis: {
      type: 'value',
      max: config.percentage ? 100 : undefined,
      axisLine: {
        lineStyle: {
          color: chart.axisLine,
        },
      },
      axisLabel: {
        color: chart.axisLabel,
        formatter: config.percentage ? '{value}%' : '{value}',
      },
      splitLine: {
        lineStyle: {
          color: chart.splitLine,
        },
      },
    },
    series: data.series?.map((s: any) => ({
      name: s.name,
      data: s.data,
      type: 'bar',
      stack: s.stack || (config.stacked ? 'total' : undefined),
    })) || [
      {
        data: data.values || [],
        type: 'bar',
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};
